import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { customFieldsRequest } from "./api";

const ReassignAdditionalDetailsBlock = ({
  teamId,
  leadId,
  context,
  onComplete,
  onBusyChange,
}) => {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const blocked = failure?.uncertain || failure?.status === 409;
  const clear = async () => {
    if (inFlight.current || blocked) {
      return;
    }
    inFlight.current = true;
    setBusy(true);
    onBusyChange?.(true);
    setFailure(null);
    try {
      await customFieldsRequest({
        teamId,
        path: `/leads/${leadId}/custom_fields/reassign_cleanup`,
        method: "POST",
        body: { context },
      });
      if (mounted.current) {
        onComplete();
      }
    } catch (error) {
      if (mounted.current) {
        setFailure(error);
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) {
        setBusy(false);
        onBusyChange?.(false);
      }
    }
  };
  let message =
    "Additional Details could not be cleared. Contact support for help.";
  if (failure?.uncertain) {
    message =
      "We couldn't confirm whether Additional Details were cleared. Return to Reassign, reload and review before trying again.";
  } else if (failure?.status === 409) {
    message =
      "Additional Details changed. Return to Reassign, reload and review before trying again.";
  } else if (failure?.code === "custom_fields_cleanup_limit") {
    message =
      "We couldn't clear Additional Details within the safe processing limit. No changes were made. Contact support for help.";
  }
  return (
    <div
      className="alert alert-warning"
      role="region"
      aria-label="Reassignment cleanup"
    >
      <p>
        Clear all Additional Details values owned by this account, including
        Required fields and retained archived values. This does not reassign the
        contact.
      </p>
      {failure && (
        <p role="alert" tabIndex={-1} ref={(node) => node?.focus()}>
          {message}
        </p>
      )}
      <button
        type="button"
        className="btn btn-danger"
        disabled={busy || blocked}
        onClick={clear}
      >
        {busy ? "Clearing…" : "Clear Additional Details"}
      </button>
    </div>
  );
};
ReassignAdditionalDetailsBlock.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  context: PropTypes.string.isRequired,
  onComplete: PropTypes.func.isRequired,
  onBusyChange: PropTypes.func,
};
export default ReassignAdditionalDetailsBlock;
